import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/useToast'
import { FileCode, Plus, Search } from 'lucide-react'

const statusOptions = ['Draft', 'Published', 'Deprecated'] as const

export function ProcessDefinitionsPage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedProcess, setSelectedProcess] = useState<string | null>(null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [formState, setFormState] = useState({
    process_id: '',
    version: '1.0.0',
    module: 'SoftwareIQ',
    name: '',
    description: '',
    status: 'Draft',
    is_active: false,
    definition: '{\n  "steps": []\n}',
  })

  const { data: processList, isLoading } = useQuery({
    queryKey: ['metadata', 'processes', 'all'],
    queryFn: () => api.metadata.processes.list({ active_only: false }),
  })

  const { data: processDetail } = useQuery({
    queryKey: ['metadata', 'processes', selectedProcess],
    queryFn: () => api.metadata.processes.get(selectedProcess!),
    enabled: !!selectedProcess,
  })

  const createMutation = useMutation({
    mutationFn: async () => {
      let definition: Record<string, unknown> = {}
      try {
        definition = JSON.parse(formState.definition)
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Invalid JSON'
        throw new Error(`Definition must be valid JSON: ${message}`)
      }

      return api.metadata.processes.create({
        process_id: formState.process_id,
        version: formState.version,
        module: formState.module,
        name: formState.name,
        description: formState.description || undefined,
        status: formState.status,
        is_active: formState.is_active,
        definition,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metadata', 'processes'] })
      setIsCreateOpen(false)
      setFormState({
        process_id: '',
        version: '1.0.0',
        module: 'SoftwareIQ',
        name: '',
        description: '',
        status: 'Draft',
        is_active: false,
        definition: '{\n  "steps": []\n}',
      })
      toast({
        title: 'Process Created',
        description: 'Process definition saved to MetadataIQ.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create process',
        variant: 'destructive',
      })
    },
  })

  const publishMutation = useMutation({
    mutationFn: (params: { processId: string; version: string }) =>
      api.metadata.processes.publish(params.processId, { version: params.version }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metadata', 'processes'] })
      toast({
        title: 'Process Published',
        description: 'The selected process is now active.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to publish process',
        variant: 'destructive',
      })
    },
  })

  const processes = processList?.processes ?? []
  const filteredProcesses = processes.filter(
    (p) =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.process_id.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold">Process Definitions</h2>
          <p className="text-muted-foreground">
            MetadataIQ workflow definitions for module-driven processes
          </p>
        </div>
        <Button variant="glow" onClick={() => setIsCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Process
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search processes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="space-y-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              filteredProcesses.map((process) => (
                <button
                  key={`${process.process_id}-${process.version}`}
                  onClick={() => setSelectedProcess(process.process_id)}
                  className={`w-full text-left p-4 rounded-lg border transition-all ${
                    selectedProcess === process.process_id
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-muted-foreground/50'
                  }`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="font-medium">{process.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {process.process_id} • v{process.version}
                      </p>
                    </div>
                    <Badge variant={process.is_active ? 'default' : 'secondary'}>
                      {process.is_active ? 'Active' : process.status}
                    </Badge>
                  </div>
                </button>
              ))
            )}

            {filteredProcesses.length === 0 && !isLoading && (
              <div className="text-center py-8 text-muted-foreground">
                <FileCode className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No processes found</p>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2">
          {selectedProcess && processDetail ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{processDetail.name}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {processDetail.module} • v{processDetail.version}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={processDetail.is_active ? 'default' : 'secondary'}>
                      {processDetail.is_active ? 'Active' : processDetail.status}
                    </Badge>
                    {!processDetail.is_active && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          publishMutation.mutate({
                            processId: processDetail.process_id,
                            version: processDetail.version,
                          })
                        }
                        disabled={publishMutation.isPending}
                      >
                        Publish
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Description
                  </h4>
                  <p className="text-sm mt-2 text-muted-foreground">
                    {processDetail.description || 'No description provided.'}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Definition JSON
                  </h4>
                  <pre className="mt-2 rounded-lg border border-border bg-muted/30 p-4 text-xs overflow-auto">
                    {JSON.stringify(processDetail.definition, null, 2)}
                  </pre>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="h-full flex items-center justify-center min-h-[400px]">
              <div className="text-center text-muted-foreground">
                <FileCode className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select a process to view details</p>
              </div>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Process Definition</DialogTitle>
            <DialogDescription>
              Define a new workflow process. JSON definition drives the UI stepper.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="process_id">Process ID</Label>
              <Input
                id="process_id"
                value={formState.process_id}
                onChange={(event) => setFormState((prev) => ({ ...prev, process_id: event.target.value }))}
                placeholder="jfac-2019"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="version">Version</Label>
                <Input
                  id="version"
                  value={formState.version}
                  onChange={(event) => setFormState((prev) => ({ ...prev, version: event.target.value }))}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="module">Module</Label>
                <Input
                  id="module"
                  value={formState.module}
                  onChange={(event) => setFormState((prev) => ({ ...prev, module: event.target.value }))}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="name">Display Name</Label>
              <Input
                id="name"
                value={formState.name}
                onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formState.description}
                onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Status</Label>
                <Select
                  value={formState.status}
                  onValueChange={(value) => setFormState((prev) => ({ ...prev, status: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status..." />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Checkbox
                  id="is_active"
                  checked={formState.is_active}
                  onCheckedChange={(checked) =>
                    setFormState((prev) => ({ ...prev, is_active: checked === true }))
                  }
                />
                <Label htmlFor="is_active">Set as active</Label>
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="definition">Definition JSON</Label>
              <Textarea
                id="definition"
                value={formState.definition}
                onChange={(event) => setFormState((prev) => ({ ...prev, definition: event.target.value }))}
                className="font-mono text-xs min-h-[180px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
              Create Process
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
